/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { type ReactElement } from 'react';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useHasSeenOnboarding } from './useHasSeenOnboarding.tsx';

const productSettingsIncidentManagementMenuItemSpotlightHeading = defineMessage({
	id: 'servicedesk-incident-management-settings.common.ui.setting-menu-onboarding.product-settings-incident-management-menu-item-spotlight-heading',
	defaultMessage: 'Save time during incidents with templates',
	description:
		'Heading for spotlight message in product settings pages for menu item of incident management',
});

const productSettingsIncidentManagementMenuItemSpotlightDescription = defineMessage({
	id: 'servicedesk-incident-management-settings.common.ui.setting-menu-onboarding.product-settings-incident-management-menu-item-spotlight-description',
	defaultMessage:
		'Create and customize email templates to save time and send consistent stakeholder notifications during incidents.',
	description:
		'Description for spotlight message in product settings pages for menu item of incident management',
});

const productSettingsIncidentManagementMenuItemSpotlightActionLabel = defineMessage({
	id: 'servicedesk-incident-management-settings.common.ui.setting-menu-onboarding.product-settings-incident-management-menu-item-spotlight-action-label',
	defaultMessage: 'OK',
	description:
		'CTA action label for spotlight message in product settings pages for menu item of incident management',
});

const SPOTLIGHT_KEY =
	'jira.servicedesk.incident-management.incident-management-settings.setting-menu-onboarding';

const IncidentManagementProductSettingNavMenuItemOnboarding = ({
	children,
	isFullyExpanded,
}: {
	children: ReactElement;
	isFullyExpanded: boolean;
}): React.JSX.Element => {
	const appEditions = useAppEditions();
	const [isOnboardingActive, setHasSeenOnboarding] = useHasSeenOnboarding(SPOTLIGHT_KEY);
	const { formatMessage } = useIntl();
	const hasPremiumServiceDeskAccess = appEditions.serviceDesk === PREMIUM_EDITION;

	return (
		<>
			<SpotlightTarget name="jira-servicedesk-incident-management-product-setting-menu-item">
				{children}
			</SpotlightTarget>
			<SpotlightTransition>
				{isOnboardingActive && isFullyExpanded && hasPremiumServiceDeskAccess && (
					<JiraSpotlight
						actions={[
							{
								onClick: setHasSeenOnboarding,
								text: formatMessage(productSettingsIncidentManagementMenuItemSpotlightActionLabel),
							},
						]}
						heading={formatMessage(productSettingsIncidentManagementMenuItemSpotlightHeading)}
						target="jira-servicedesk-incident-management-product-setting-menu-item"
						key="jira-servicedesk-incident-management-product-setting-menu-item"
						targetRadius={3}
						targetBgColor={token('elevation.surface')}
						messageType="engagement"
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId="servicedesk-incident-management-settings.common.ui.setting-menu-onboarding.jira-spotlight.jira-servicedesk-incident-management-product-setting-menu-item"
						dialogPlacement="right top"
					>
						{formatMessage(productSettingsIncidentManagementMenuItemSpotlightDescription)}
					</JiraSpotlight>
				)}
			</SpotlightTransition>
		</>
	);
};

export default IncidentManagementProductSettingNavMenuItemOnboarding;
